import type { Entity } from '@api/api/entity-schema';
import { ExploreInsightDirection } from '@api/api/explore-filter-schema';
import type { DetailedInsight } from '@api/api/insights';
import { formatTicker } from '@api/mappers/formatTicker';
import { formatPriceWithCurrency } from '@helpers/utils/currency/currency';
import { formatUtcDate } from '@terminal/services/insights/utils/insight-utils';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import { t } from 'i18next';

const KIND_LABEL_KEYS = {
  fundamentals: 'insights:tooltip.label.fundamentals',
  technicals: 'insights:tooltip.label.technicals',
  macroeconomics: 'insights:tooltip.label.macroeconomics',
  combination: 'insights:tooltip.label.combination',
} as const;

type KindLabelKey = keyof typeof KIND_LABEL_KEYS;

const isKindLabelKey = (value: string): value is KindLabelKey =>
  Object.hasOwn(KIND_LABEL_KEYS, value);

export const getInsightDrawerCategoryLabel = (insight: DetailedInsight) => {
  const direction = getExploreDirection(insight.metadata.direction);
  const directionLabel =
    direction === ExploreInsightDirection.Bullish
      ? t('widget:insights.insightDirection_bullish')
      : t('widget:insights.insightDirection_bearish');

  const { kind } = insight.metadata;
  const categoryKey =
    kind && isKindLabelKey(kind) ? KIND_LABEL_KEYS[kind] : undefined;
  const categoryLabel = categoryKey ? t(categoryKey) : null;

  return categoryLabel
    ? `${directionLabel} · ${categoryLabel}`
    : directionLabel;
};

export const getInsightDrawerReferenceLabel = (
  insight: DetailedInsight,
  entity: Entity
) => {
  const lastValueDate = insight.metadata.last_value_date;

  if (!lastValueDate) {
    return undefined;
  }

  const ticker = entity.ticker
    ? formatTicker(entity)
    : entity.name_short || entity.name_full;

  return t('widget:insights.scenarioReferenceMetadata', {
    ticker,
    price: formatPriceWithCurrency(
      insight.metadata.last_value,
      insight.metadata.currency
    ),
    lastValueDate: formatUtcDate(lastValueDate, 'PP'),
  });
};
